import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Zap,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeftRight,
  FileText,
  Copy,
  ChevronDown,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
} from "lucide-react";
import { apiCall } from "../api";
import * as pdfjsLib from "pdfjs-dist";
import "./V1ComparisonDetail.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

// Model type display config
const MODEL_CONFIG = {
  xorbit: {
    name: "XOrbit",
    description: "Gemini 3.5 Flash",
    color: "#8b5cf6",
    bgColor: "#f3e8ff",
  },
  vja: {
    name: "VJA",
    description: "Qwen VL",
    color: "#0ea5e9",
    bgColor: "#e0f2fe",
  },
};

// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = true, color }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="v1-detail-collapsible" style={{ "--section-color": color }}>
      <button className="v1-detail-section-header" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span>{title}</span>
      </button>
      {isOpen && <div className="v1-detail-section-content">{children}</div>}
    </div>
  );
};

// Difference indicator
const DiffIndicator = ({ match }) => {
  if (match === true) {
    return <CheckCircle size={14} className="diff-match" />;
  } else if (match === false) {
    return <XCircle size={14} className="diff-mismatch" />;
  }
  return <AlertCircle size={14} className="diff-unknown" />;
};

const V1ComparisonDetail = () => {
  const { comparisonId } = useParams();
  const navigate = useNavigate();
  const pdfContainerRef = useRef(null);

  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedField, setCopiedField] = useState(null);
  const [viewMode, setViewMode] = useState("all"); // 'all', 'matches', 'differences'
  
  // PDF state
  const [pdfDocument, setPdfDocument] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfScale, setPdfScale] = useState(1.0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (comparisonId) {
      fetchComparison();
    }
  }, [comparisonId]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await apiCall(`/v1/comparisons/${comparisonId}`);
      setComparison(data);
      
      // Load PDF if available
      if (data.file_id || data.pdf_url) {
        loadPDF(data.file_id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPDF = async (fileId) => {
    try {
      setPdfLoading(true);
      const response = await fetch(`/v1/comparisons/${comparisonId}/pdf`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      if (!response.ok) throw new Error("Failed to load PDF");

      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      setPdfDocument(pdf);
      setTotalPages(pdf.numPages);
    } catch (err) {
      console.error("PDF load error:", err);
      // Don't show error for PDF - it might not be available
    } finally {
      setPdfLoading(false);
    }
  };

  // Render PDF pages
  useEffect(() => {
    if (pdfDocument && pdfContainerRef.current) {
      renderPDFPages();
    }
  }, [pdfDocument, pdfScale]);

  const renderPDFPages = async () => {
    if (!pdfDocument || !pdfContainerRef.current) return;

    const container = pdfContainerRef.current;
    container.innerHTML = "";

    for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
      try {
        const page = await pdfDocument.getPage(pageNum);
        const viewport = page.getViewport({ scale: pdfScale });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.className = "v1-pdf-page";
        canvas.dataset.page = pageNum;

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;

        // Add page number label
        const pageLabel = document.createElement("div");
        pageLabel.className = "v1-pdf-page-label";
        pageLabel.textContent = `Page ${pageNum}`;

        const pageWrapper = document.createElement("div");
        pageWrapper.className = "v1-pdf-page-wrapper";
        pageWrapper.appendChild(canvas);
        pageWrapper.appendChild(pageLabel);

        container.appendChild(pageWrapper);
      } catch (err) {
        console.error(`Error rendering page ${pageNum}:`, err);
      }
    }
  };

  const copyToClipboard = useCallback((text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  }, []);

  const downloadJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate match statistics
  const calculateStats = () => {
    if (!comparison?.comparison_result) return { total: 0, matches: 0, mismatches: 0 };

    let total = 0;
    let matches = 0;

    const countFields = (obj) => {
      if (!obj || typeof obj !== "object") return;
      
      for (const [key, value] of Object.entries(obj)) {
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          if ("match" in value) {
            total++;
            if (value.match) matches++;
          } else {
            countFields(value);
          }
        }
      }
    };

    countFields(comparison.comparison_result);
    return { total, matches, mismatches: total - matches };
  };

  const stats = calculateStats();
  const matchPercentage = stats.total > 0 ? Math.round((stats.matches / stats.total) * 100) : 0;

  // Render comparison rows
  const renderComparisonRows = (data, prefix = "") => {
    if (!data || typeof data !== "object") return [];

    const rows = [];
    
    Object.entries(data).forEach(([key, value]) => {
      const fieldPath = prefix ? `${prefix}.${key}` : key;

      // Check if this is a comparison object with xorbit/vja values
      if (value && typeof value === "object" && "xorbit" in value && "vja" in value) {
        const isMatch = value.match;
        
        // Filter based on viewMode
        if (viewMode === "matches" && !isMatch) return;
        if (viewMode === "differences" && isMatch) return;

        rows.push(
          <tr key={fieldPath} className={isMatch ? "row-match" : "row-mismatch"}>
            <td className="field-name">
              <DiffIndicator match={isMatch} />
              <span>{key}</span>
            </td>
            <td className="field-value xorbit">
              <span>{String(value.xorbit ?? "-")}</span>
              <button
                className="v1-copy-btn-mini"
                onClick={() => copyToClipboard(String(value.xorbit ?? ""), `${fieldPath}-xorbit`)}
              >
                <Copy size={12} />
                {copiedField === `${fieldPath}-xorbit` && <span className="copied-tooltip">Copied!</span>}
              </button>
            </td>
            <td className="field-value vja">
              <span>{String(value.vja ?? "-")}</span>
              <button
                className="v1-copy-btn-mini"
                onClick={() => copyToClipboard(String(value.vja ?? ""), `${fieldPath}-vja`)}
              >
                <Copy size={12} />
                {copiedField === `${fieldPath}-vja` && <span className="copied-tooltip">Copied!</span>}
              </button>
            </td>
          </tr>
        );
      } else if (value && typeof value === "object" && !Array.isArray(value)) {
        // Nested section
        const nestedRows = renderComparisonRows(value, fieldPath);
        if (nestedRows.length > 0) {
          rows.push(
            <tr key={`${fieldPath}-header`} className="section-header-row">
              <td colSpan="3">{key}</td>
            </tr>
          );
          rows.push(...nestedRows);
        }
      }
    });

    return rows;
  };

  if (loading) {
    return (
      <div className="v1-comparison-detail-loading">
        <RefreshCw size={32} className="spin" />
        <p>Loading comparison...</p>
      </div>
    );
  }

  if (error && !comparison) {
    return (
      <div className="v1-comparison-detail-error">
        <XCircle size={48} />
        <h3>Error Loading Comparison</h3>
        <p>{error}</p>
        <button onClick={() => navigate("/v1/comparison")}>Go Back</button>
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="v1-comparison-detail-error">
        <AlertCircle size={48} />
        <h3>Comparison Not Found</h3>
        <button onClick={() => navigate("/v1/comparison")}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="v1-comparison-detail">
      {/* Back Button */}
      <button className="v1-back-btn" onClick={() => navigate("/v1/comparison")}>
        <ArrowLeft size={18} />
        Back to Comparisons
      </button>

      {/* Header */}
      <div className="v1-detail-header">
        <div className="v1-detail-header-left">
          <h1>
            <ArrowLeftRight size={24} />
            {comparison.filename || "Comparison"}
          </h1>
          <div className="v1-detail-meta">
            <span>ID: {comparison._id}</span>
            <span>Created: {formatDate(comparison.created_at)}</span>
          </div>
        </div>
        <div className="v1-detail-header-right">
          <div
            className="v1-match-percentage-badge"
            data-level={matchPercentage >= 80 ? "high" : matchPercentage >= 50 ? "medium" : "low"}
          >
            {matchPercentage >= 80 ? <CheckCircle size={18} /> : matchPercentage >= 50 ? <AlertCircle size={18} /> : <XCircle size={18} />}
            <span>{matchPercentage}% Match</span>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="v1-detail-stats">
        <div className="v1-detail-stat total">
          <FileText size={20} />
          <div>
            <span className="value">{stats.total}</span>
            <span className="label">Total Fields</span>
          </div>
        </div>
        <div className="v1-detail-stat matches">
          <CheckCircle size={20} />
          <div>
            <span className="value">{stats.matches}</span>
            <span className="label">Matches</span>
          </div>
        </div>
        <div className="v1-detail-stat differences">
          <XCircle size={20} />
          <div>
            <span className="value">{stats.mismatches}</span>
            <span className="label">Differences</span>
          </div>
        </div>
        <div className="v1-detail-stat xorbit-time">
          <Zap size={20} style={{ color: MODEL_CONFIG.xorbit.color }} />
          <div>
            <span className="value">{comparison.xorbit_time?.toFixed(2) || "-"}s</span>
            <span className="label">XOrbit Time</span>
          </div>
        </div>
        <div className="v1-detail-stat vja-time">
          <Zap size={20} style={{ color: MODEL_CONFIG.vja.color }} />
          <div>
            <span className="value">{comparison.vja_time?.toFixed(2) || "-"}s</span>
            <span className="label">VJA Time</span>
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="v1-detail-split-view">
        {/* Left Side - Extraction Data */}
        <div className="v1-detail-left-panel">
          <div className="v1-detail-panel-header">
            <h3>Extraction Comparison</h3>
            <div className="v1-detail-view-toggle">
              <button className={viewMode === "all" ? "active" : ""} onClick={() => setViewMode("all")}>
                All
              </button>
              <button className={viewMode === "matches" ? "active" : ""} onClick={() => setViewMode("matches")}>
                Matches
              </button>
              <button className={viewMode === "differences" ? "active" : ""} onClick={() => setViewMode("differences")}>
                Differences
              </button>
            </div>
          </div>

          {/* Download Buttons */}
          <div className="v1-detail-download-row">
            <button
              className="v1-download-btn xorbit"
              onClick={() => downloadJSON(comparison.xorbit_result, `xorbit-${comparison.filename || comparison._id}.json`)}
            >
              <Download size={14} />
              <Zap size={14} style={{ color: MODEL_CONFIG.xorbit.color }} />
              XOrbit JSON
            </button>
            <button
              className="v1-download-btn vja"
              onClick={() => downloadJSON(comparison.vja_result, `vja-${comparison.filename || comparison._id}.json`)}
            >
              <Download size={14} />
              <Zap size={14} style={{ color: MODEL_CONFIG.vja.color }} />
              VJA JSON
            </button>
          </div>

          {/* Comparison Table */}
          <div className="v1-detail-table-container">
            <table className="v1-detail-table">
              <thead>
                <tr>
                  <th className="th-field">Field</th>
                  <th className="th-xorbit">
                    <Zap size={14} style={{ color: MODEL_CONFIG.xorbit.color }} />
                    XOrbit
                  </th>
                  <th className="th-vja">
                    <Zap size={14} style={{ color: MODEL_CONFIG.vja.color }} />
                    VJA
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.comparison_result ? (
                  renderComparisonRows(comparison.comparison_result)
                ) : (
                  <tr>
                    <td colSpan="3" className="no-data">No comparison data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Side - PDF Viewer */}
        <div className="v1-detail-right-panel">
          <div className="v1-detail-panel-header">
            <h3>Original Document</h3>
            <div className="v1-pdf-controls">
              <button onClick={() => setPdfScale(Math.max(0.5, pdfScale - 0.1))} title="Zoom Out">
                <ZoomOut size={16} />
              </button>
              <span className="v1-pdf-zoom-level">{Math.round(pdfScale * 100)}%</span>
              <button onClick={() => setPdfScale(Math.min(2, pdfScale + 0.1))} title="Zoom In">
                <ZoomIn size={16} />
              </button>
              <button onClick={() => setPdfScale(1)} title="Reset Zoom">
                <RotateCw size={16} />
              </button>
            </div>
          </div>

          <div className="v1-pdf-viewer">
            {pdfLoading ? (
              <div className="v1-pdf-loading">
                <RefreshCw size={24} className="spin" />
                <p>Loading PDF...</p>
              </div>
            ) : pdfDocument ? (
              <div className="v1-pdf-pages" ref={pdfContainerRef}></div>
            ) : (
              <div className="v1-pdf-empty">
                <FileText size={48} />
                <p>PDF preview not available</p>
              </div>
            )}
          </div>

          {totalPages > 0 && (
            <div className="v1-pdf-footer">
              <span>{totalPages} page{totalPages > 1 ? "s" : ""}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default V1ComparisonDetail;
